import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { firms as firmsService } from "../../api/firms.service";
import { GenericForm, type GenericFormHandle } from "../../components/Form/forms/GenericForm";
import { SubscriptionMetadataConfig } from "../../components/SubscriptionMetadataConfig";
import { LANGUAGE_LABELS } from "../../constants/features";
import { HISTORICAL_DATA_GLOBAL_HINT } from "../../constants/historicalData";
import { withGlobalRegionFirst } from "../../hooks/useJurisdictionOptions";
import { SCOPE_NAMES } from "../../constants/scopes";
import type { Subscription } from "../../schemas/subscriptionSchema";
import type { SubscriptionWithFeatures } from "../../api/subscriptions.service";
import type { Entity } from "../../components/EntityPicker";
import {
  createSubscriptionSchema,
  editSubscriptionSchema,
} from "../../schemas/subscriptionValidation";
import type { FormFieldConfig } from "../../types/uiTypes";
import {
  usePackageOptions,
  useRegionOptions,
  useInitialFormOptions,
  usePackageDetails,
  useOfficeOptions,
  useContactOptions,
  useEditorialCategories,
  useSubscriptionLocationOptions,
} from "./hooks";

export type EntityOption = {
  value: number | string;
  label: string;
  description?: string;
};

type SubscriptionFormProps = {
  subscription: Subscription | SubscriptionWithFeatures | null;
  onSave: (data: Partial<Subscription>) => Promise<void>;
  onCancel: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  submitLabel?: string;
  skipMetadataValidation?: boolean;
  onPackageChange?: (
    packageId: string,
    metadataQuantities: Record<string, number> | null,
    packageFeatures: Array<{
      entity_id_field: string | null;
      entity_id_label: string | null;
      entity_id_help_text: string | null;
    }> | null,
    contactOptions: Entity[],
    editorialCategoryOptions: Array<{ value: string | number; label: string }>,
  ) => void;
};

export function SubscriptionForm({
  subscription,
  onSave,
  onCancel,
  onDirtyChange,
  submitLabel = "Save",
  onPackageChange,
}: SubscriptionFormProps) {
  const isEditMode = subscription !== null;
  const validationSchema = isEditMode ? editSubscriptionSchema : createSubscriptionSchema;
  const formRef = useRef<GenericFormHandle>(null);

  // Use hooks for data fetching
  const { packageOptions } = usePackageOptions();
  const { regionOptions } = useRegionOptions();
  const {
    regionOptions: locationRegionOptions,
    countryOptions: locationCountryOptions,
    allOptions: allLocationOptions,
    detectLocationType,
  } = useSubscriptionLocationOptions();
  const { initialFirmOption } = useInitialFormOptions(subscription);

  type LocationType = "region" | "country";
  const [locationType, setLocationType] = useState<LocationType>("country");

  useEffect(() => {
    if (subscription?.siteStructureId && locationRegionOptions.length > 0) {
      const detected = detectLocationType(String(subscription.siteStructureId));
      setLocationType(detected);
      formRef.current?.form.setValue("locationType", detected);
    }
  }, [subscription?.siteStructureId, locationRegionOptions.length, detectLocationType]);

  // Extract initial values for hooks
  const initialPracticeGroupIds = subscription?.metadata?.practice_group_ids as
    | (number | string)[]
    | undefined;

  const {
    editorialCategoryOptions,
    practiceGroupIdMap,
    selectedPracticeGroups,
    handleRegionChange,
    handlePracticeGroupChange,
  } = useEditorialCategories(subscription?.siteStructureId, initialPracticeGroupIds);

  // Track selected firmId and country for passing to child components
  const [selectedFirmId, setSelectedFirmId] = useState<number | string | null>(
    subscription?.firmId ?? null,
  );
  const [selectedCountryId, setSelectedCountryId] = useState<string | null>(
    subscription?.siteStructureId ? String(subscription.siteStructureId) : null,
  );
  const selectedCountryName = useMemo(() => {
    if (!selectedCountryId) return null;
    return allLocationOptions.find(c => c.value === selectedCountryId)?.label ?? null;
  }, [selectedCountryId, allLocationOptions]);
  const { contactOptions } = useContactOptions(subscription?.firmId);
  const { officeOptions, handleFirmChange: baseHandleFirmChange } = useOfficeOptions(
    subscription?.firmId,
    selectedCountryName,
  );

  // Wrap firm change to track selected firmId
  const handleFirmChange = useCallback(
    async (firmId: number) => {
      setSelectedFirmId(firmId);
      await baseHandleFirmChange(firmId);
    },
    [baseHandleFirmChange],
  );

  // Wrap onPackageChange to include contactOptions (adapts 4-param hook callback to 5-param parent callback)
  const wrappedOnPackageChange = useCallback(
    (
      packageId: string,
      quantities: Record<string, number> | null,
      features: Array<{
        entity_id_field: string | null;
        entity_id_label: string | null;
        entity_id_help_text: string | null;
      }> | null,
      editorialOptions: Array<{ value: string | number; label: string }>,
    ) => {
      onPackageChange?.(packageId, quantities, features, contactOptions, editorialOptions);
    },
    [onPackageChange, contactOptions],
  );

  const {
    selectedPackageId,
    availableSites,
    availableLanguages,
    metadataQuantities,
    packageFeatures,
    defaultSiteStructureId,
    includesHistoricalData,
    handlePackageChange: baseHandlePackageChange,
  } = usePackageDetails(subscription?.packageId, {
    onPackageChange: wrappedOnPackageChange,
    editorialCategoryOptions,
  });

  // When a package has a defaultSiteStructureId, auto-set and lock the location field
  useEffect(() => {
    if (defaultSiteStructureId) {
      const displayName =
        defaultSiteStructureId in SCOPE_NAMES
          ? SCOPE_NAMES[defaultSiteStructureId]
          : defaultSiteStructureId;
      formRef.current?.form.setValue("siteStructureId", defaultSiteStructureId);
      formRef.current?.form.setValue("billingTaxonomyTermId", defaultSiteStructureId);
      formRef.current?.form.setValue("siteStructureIdDisplay", displayName);
      formRef.current?.form.setValue("locationType", "region");
      setLocationType("region");
      setSelectedCountryId(defaultSiteStructureId);
    } else {
      formRef.current?.form.setValue("siteStructureIdDisplay", "");
    }
  }, [defaultSiteStructureId]);

  useEffect(() => {
    if (!includesHistoricalData || defaultSiteStructureId) return;
    if (formRef.current?.form.getValues("siteStructureId")) return;
    formRef.current?.form.setValue("locationType", "region");
    setLocationType("region");
  }, [includesHistoricalData, defaultSiteStructureId]);

  // Wrap package change to notify parent with current options
  const handlePackageChangeWithCallback = useCallback(
    async (packageId: string) => {
      await baseHandlePackageChange(packageId);
    },
    [baseHandlePackageChange],
  );

  const formFields = useMemo(() => {
    const fields: Array<
      FormFieldConfig & {
        onChange?: (
          value: unknown,
          allValues: Record<string, unknown>,
          setValue: (name: string, value: unknown) => void,
        ) => void;
      }
    > = [
      {
        name: "firmId",
        label: "Company",
        type: "asyncSearchableSelect",
        required: true,
        onSearch: async (query: string) => {
          const isSlugSearch = query.includes("/") || query.match(/^\d+-/);
          const response = isSlugSearch
            ? await firmsService.getBySlug(query, 50)
            : await firmsService.getAll({ search: query, limit: 50 });
          return response.companies.map(company => ({
            value: company.companyId,
            label: `${company.name} (ID: ${company.companyId})`,
            description: company.slug || undefined,
          }));
        },
        debounceMs: 300,
        minSearchLength: 2,
        placeholder: "Type to search companies...",
        helperText: "Search for the company that purchased this subscription",
        initialOption: initialFirmOption,
        onChange: async (value, _allValues, setValue) => {
          if (value) {
            const firmId = Number(value);
            await handleFirmChange(firmId);
            const company = await firmsService.getById(firmId);
            setValue("companySlug", company?.slug || "");
            setValue("crmAccountId", company?.crmEntityNumber || "");
          } else {
            setValue("companySlug", "");
            setValue("crmAccountId", "");
          }
          setValue("metadata.office_contact_pairs", []);
        },
      },
      {
        name: "companySlug",
        label: "Company Slug",
        type: "text",
        disabled: true,
        placeholder: "Selected company slug will appear here",
        helperText: "The URL slug for this company on the site (read-only)",
      },
      {
        name: "crmAccountId",
        label: "CRM Account ID",
        type: "text",
        disabled: true,
        placeholder: "CRM account ID will appear here",
        helperText: "The CRM entity number for this company (read-only)",
      },
      {
        name: "packageId",
        label: "Package",
        type: "searchableSelect",
        required: true,
        options: packageOptions,
        helperText: "The package that defines which features are included",
        onChange: async (value, _allValues, setValue) => {
          await handlePackageChangeWithCallback(String(value));
          setValue("metadata.office_contact_pairs", []);
          setValue("metadata.editorial_category_ids", []);
        },
      },
      {
        name: "locationType",
        label: "Location Type",
        type: "radio",
        required: false,
        disabled: defaultSiteStructureId !== null,
        options: [
          { value: "region", label: "Region" },
          { value: "country", label: "Country" },
        ],
        helperText: "Choose whether this subscription targets a region or a specific country",
        onChange: (value, _allValues, setValue) => {
          setLocationType(value as LocationType);
          setValue("siteStructureId", "");
          setSelectedCountryId(null);
        },
      },
      {
        name: "siteStructureId",
        label: defaultSiteStructureId
          ? "Location"
          : locationType === "region"
            ? "Region"
            : "Country",
        type: defaultSiteStructureId ? "text" : "searchableSelect",
        required: true,
        disabled: defaultSiteStructureId !== null,
        options: defaultSiteStructureId
          ? undefined
          : locationType === "region"
            ? includesHistoricalData
              ? withGlobalRegionFirst(locationRegionOptions)
              : locationRegionOptions
            : locationCountryOptions,
        preserveOptionOrder: locationType === "region" && includesHistoricalData,
        placeholder:
          locationType === "region" ? "Search for a region..." : "Search for a country...",
        helperText: [
          locationType === "region"
            ? "The region where features will appear on the site"
            : "The country where features will appear on the site",
          includesHistoricalData ? HISTORICAL_DATA_GLOBAL_HINT : null,
        ]
          .filter(Boolean)
          .join(". "),
        onChange: async (value, allValues, setValue) => {
          setSelectedCountryId(value !== null ? String(value) : null);
          await handleRegionChange(String(value));
          setValue("metadata.editorial_category_ids", []);
          setValue("metadata.office_contact_pairs", []);
          if (allValues.billingSameAsPlacement === "yes") {
            setValue("billingTaxonomyTermId", value);
          }
        },
      },
      {
        name: "billingSameAsPlacement",
        label: "Is billing region the same as placement country?",
        type: "radio",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
        helperText: "Most subscriptions have the same billing and placement region",
        onChange: (value, allValues, setValue) => {
          if (value === "yes" && allValues.siteStructureId) {
            setValue("billingTaxonomyTermId", allValues.siteStructureId);
          }
        },
      },
      {
        name: "billingTaxonomyTermId",
        label: "Billing Region",
        type: "searchableSelect",
        required: true,
        hidden: (allValues: Record<string, unknown>) => allValues.billingSameAsPlacement === "yes",
        options: regionOptions,
        placeholder: "Search for a region...",
        helperText: "The region where this subscription is billed (for reporting purposes)",
      },
      {
        name: "site",
        label: "Site",
        type: "select",
        required: true,
        options: [
          { value: "", label: "Select a site..." },
          ...availableSites.map(x => ({ value: x, label: x })),
        ],
        helperText:
          availableSites.length > 0
            ? `Available sites based on selected package (${availableSites.length} available)`
            : "Select a package first to see available sites",
      },
      {
        name: "language",
        label: "Language",
        type: "select",
        required: true,
        options: [
          { value: "", label: "Select a language..." },
          ...availableLanguages.map(lang => ({
            value: lang,
            label: LANGUAGE_LABELS[lang] || lang,
          })),
        ],
        helperText:
          availableLanguages.length > 0
            ? `Available languages based on selected package (${availableLanguages.length} available)`
            : "Select a package first to see available languages",
      },
      {
        name: "startDate",
        label: "Start Date",
        type: "date",
        required: true,
        helperText: "When the subscription becomes active",
      },
      {
        name: "endDate",
        label: "End Date",
        type: "date",
        required: true,
        helperText: "When the subscription expires",
      },
      {
        name: "status",
        label: "Status",
        type: "select",
        required: true,
        disabled: true,
        options: [
          { value: "pending", label: "Pending" },
          { value: "complete", label: "Complete" },
          { value: "cancelled", label: "Cancelled" },
        ],
        helperText:
          "Automatically derived from onboarding metadata completeness — not editable here",
      },
      {
        name: "correlationId",
        label: "Correlation ID",
        type: "text",
        placeholder: "Enter Correlation ID (optional)",
        helperText: "Optional unique identifier for idempotency (e.g., CRM order ID)",
      },
    ];

    return fields;
  }, [
    isEditMode,
    availableSites,
    availableLanguages,
    regionOptions,
    packageOptions,
    locationRegionOptions,
    locationCountryOptions,
    locationType,
    defaultSiteStructureId,
    includesHistoricalData,
    initialFirmOption,
    handleFirmChange,
    handlePackageChangeWithCallback,
    handleRegionChange,
  ]);

  const defaultValues = useMemo(() => {
    if (!subscription) {
      return {
        firmId: "",
        packageId: "",
        locationType: "country",
        siteStructureId: "",
        siteStructureIdDisplay: "",
        billingSameAsPlacement: "yes",
        billingTaxonomyTermId: "",
        site: "legal500.com",
        language: "en",
        startDate: "",
        endDate: "",
        status: "pending",
        correlationId: "",
        companySlug: "",
        crmAccountId: "",
        metadata: {
          contact_ids: [],
          editorial_category_ids: [],
          office_id: undefined,
          office_contact_pairs: [],
        },
      };
    }

    const isSame =
      String(subscription.billingTaxonomyTermId) === String(subscription.siteStructureId);

    return {
      firmId: subscription.firmId,
      packageId: subscription.packageId,
      locationType: subscription.siteStructureId
        ? detectLocationType(String(subscription.siteStructureId))
        : "country",
      siteStructureId: subscription.siteStructureId,
      siteStructureIdDisplay: "",
      billingSameAsPlacement: isSame ? "yes" : "no",
      billingTaxonomyTermId: String(subscription.billingTaxonomyTermId),
      site: subscription.site,
      language: subscription.language,
      startDate: subscription.startDate,
      endDate: subscription.endDate,
      status: subscription.status,
      correlationId: subscription.correlationId ?? "",
      companySlug: (subscription as SubscriptionWithFeatures).companySlug ?? "",
      crmAccountId: (subscription as SubscriptionWithFeatures).companyCrmEntityNumber ?? "",
      metadata: {
        contact_ids: subscription.metadata?.contact_ids || [],
        editorial_category_ids:
          subscription.metadata?.practice_area_ids ||
          subscription.metadata?.editorial_category_ids ||
          [],
        office_id: subscription.metadata?.office_id,
        office_contact_pairs: subscription.metadata?.office_contact_pairs || [],
      },
    };
  }, [subscription, detectLocationType]);

  const handleSave = async (data: Record<string, unknown>) => {
    if (data.billingSameAsPlacement === "yes") {
      data.billingTaxonomyTermId = data.siteStructureId;
    }

    if (data.siteStructureId !== undefined && data.siteStructureId !== null) {
      data.siteStructureId = String(data.siteStructureId);
    }

    // Remove UI-only fields that the API doesn't accept
    delete data.companySlug;
    delete data.crmAccountId;
    delete data.billingSameAsPlacement;
    delete data.locationType;
    delete data.siteStructureIdDisplay;

    const metadata = data.metadata as Record<string, unknown> | undefined;
    if (metadata) {
      const cleanedMetadata: Record<string, unknown> = {};

      for (const [key, value] of Object.entries(metadata)) {
        // Handle office_id as a single number value
        if (key === "office_id" && typeof value === "number") {
          cleanedMetadata[key] = value;
          continue;
        }
        // Handle office_contact_pairs as array of objects [{ office_id, contact_ids }]
        if (key === "office_contact_pairs" && Array.isArray(value)) {
          // Filter out entries without valid office_id or contact_ids
          const validContacts = value.filter(
            (item: { office_id?: number | null; contact_ids?: number[] }) =>
              item.office_id != null &&
              Array.isArray(item.contact_ids) &&
              item.contact_ids.length > 0,
          );
          if (validContacts.length > 0) {
            cleanedMetadata[key] = validContacts;
          }
          continue;
        }
        // Handle arrays (contact_ids, editorial_category_ids)
        if (!Array.isArray(value) || value.length === 0) continue;
        cleanedMetadata[key] = value;
      }

      data.metadata = Object.keys(cleanedMetadata).length > 0 ? cleanedMetadata : undefined;
    }

    await onSave(data);
  };

  const handlePracticeGroupChangeWrapper = useCallback(
    (groups: (number | string)[], _areas: (number | string)[]) => {
      handlePracticeGroupChange(groups);
    },
    [handlePracticeGroupChange],
  );

  return (
    <GenericForm
      ref={formRef}
      fields={formFields}
      validationSchema={validationSchema}
      defaultValues={defaultValues}
      onSave={handleSave}
      onCancel={onCancel}
      onDirtyChange={onDirtyChange}
      submitLabel={submitLabel}
      metadataConfig={(control, errors) => (
        <SubscriptionMetadataConfig
          packageId={selectedPackageId}
          packageFeatures={packageFeatures}
          metadataQuantities={metadataQuantities}
          disabled={false}
          firmId={selectedFirmId}
          officeOptions={officeOptions}
          editorialCategoryOptions={editorialCategoryOptions}
          control={control}
          errors={errors}
          practiceGroupIdMap={practiceGroupIdMap}
          selectedPracticeGroups={selectedPracticeGroups}
          onPracticeGroupChange={handlePracticeGroupChangeWrapper}
        />
      )}
    />
  );
}
